import { useEffect, useRef, useCallback } from 'react'

const POLL_INTERVAL = 60_000 // 60 seconds
const VERSION_URL = '/version.json'

/**
 * useVersionCheck polls /version.json periodically and detects when a new
 * frontend build has been deployed. When a new version is detected, it
 * silently clears caches and reloads the page — no modal, no banner.
 */
export function useVersionCheck() {
  const currentVersion = useRef<string | null>(null)

  const applyUpdate = useCallback(() => {
    if ('caches' in window) {
      caches.keys().then(names => {
        Promise.all(names.map(n => caches.delete(n))).then(() => {
          window.location.reload()
        })
      })
    } else {
      location.reload()
    }
  }, [])

  const checkVersion = useCallback(async () => {
    try {
      const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' } })
      if (!res.ok) return
      const data = await res.json()
      if (!data.version) return

      if (currentVersion.current === null) {
        // First load — store current version, don't trigger update
        currentVersion.current = data.version
        return
      }

      if (data.version !== currentVersion.current) {
        // New version detected — silently clear cache and reload
        applyUpdate()
      }
    } catch {
      // Network error — ignore, will retry next poll
    }
  }, [applyUpdate])

  // Poll for version changes
  useEffect(() => {
    const initialTimer = setTimeout(checkVersion, 3000)
    const interval = setInterval(checkVersion, POLL_INTERVAL)

    // Also check when the tab regains focus
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkVersion()
      }
    }
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      clearTimeout(initialTimer)
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [checkVersion])

  // Always return false for hasUpdate — we don't show a banner anymore.
  // applyUpdate is kept for backwards compatibility but the hook auto-applies.
  return { hasUpdate: false, applyUpdate }
}
